// Post-trial officer survey — the single source of truth for the questions,
// their options, validation, and the aggregate shape that feeds the staff
// portal and the trial-findings brief.
//
// Synced verbatim to client/src/common/ and PoliceNarrativesMobile/common/
// by `yarn sync-survey` (part of `yarn sync-shared`, run by `yarn migrate`).
// NEVER edit the generated copies. No JSX/React, no Node built-ins, no
// imports — this file must load identically on all three platforms.

export const SURVEY_VERSION = 1;

// Officers are prompted inside the app during the final week of the trial,
// and on the paywall for up to SURVEY_POST_EXPIRY_DAYS after it ends.
export const SURVEY_FINAL_WEEK_DAYS = 7;
export const SURVEY_POST_EXPIRY_DAYS = 60;

// "I'm still evaluating" postpones the prompt this many days, never past
// the end of the trial. Only offered while the agency's trial is running;
// once it ends the survey is required.
export const SURVEY_SNOOZE_DAYS = 5;

export const SURVEY_QUOTE_MAX_CHARS = 600;
// Free text attached to an "Other" option (stored under `<questionId>_other`).
export const SURVEY_OTHER_MAX_CHARS = 300;

export type SurveyQuestionId =
  | "ai_attitude_before"
  | "ai_attitude_after"
  | "overall"
  | "quality_vs_manual"
  | "quality_improvements"
  | "time_saved_per_report"
  | "overtime"
  | "time_reinvested"
  | "ease_of_use"
  | "time_to_comfort"
  | "favorite_features"
  | "concerns"
  | "recommend"
  | "keep"
  | "quote";

// Every answerable question except the free-text quote, which is stored in
// its own columns (quote + quote_consent).
export type SurveyAnswerId = Exclude<SurveyQuestionId, "quote">;
// Key that carries the free text for an option flagged `allowText`.
export type SurveyOtherKey = `${SurveyAnswerId}_other`;
export type SurveyAnswerKey = SurveyAnswerId | SurveyOtherKey;
export const otherKey = (id: SurveyAnswerId): SurveyOtherKey => `${id}_other`;

export type SurveyOption = {
  value: string;
  label: string;
  // Minutes represented by this bucket — only on time_saved_per_report,
  // used for the weighted "minutes saved per report" figure.
  midpoint?: number;
  // Selecting this option reveals a short text box ("Other"). The text is
  // stored in answers under otherKey(questionId).
  allowText?: boolean;
};

// Prompts may contain {agency}, replaced with the agency's name (or "your
// department") by resolveSurveyCopy(). Questions that mention the trial
// carry a feedbackPrompt used when the survey runs in "feedback" mode
// (an agency that isn't on a trial, surveyed on demand by staff).
type SurveyQuestionBase = {
  id: SurveyQuestionId;
  prompt: string;
  feedbackPrompt?: string;
  // Short line under the prompt, e.g. "Pick up to 3".
  help?: string;
  feedbackHelp?: string;
};

// trial = the agency is (or just was) on a trial; feedback = staff sent
// the survey to an agency that isn't on one.
export type SurveyMode = "trial" | "feedback";

export function agencyLabel(agencyName?: string | null): string {
  return agencyName && agencyName.trim() ? agencyName.trim() : "your department";
}

export function resolveSurveyCopy(
  q: SurveyQuestion,
  ctx: { mode: SurveyMode; agencyName?: string | null }
): { prompt: string; help?: string } {
  const name = agencyLabel(ctx.agencyName);
  const fill = (s?: string) => (s === undefined ? undefined : s.replace(/\{agency\}/g, name));
  const prompt = ctx.mode === "feedback" && q.feedbackPrompt ? q.feedbackPrompt : q.prompt;
  const help = ctx.mode === "feedback" && q.feedbackHelp ? q.feedbackHelp : q.help;
  return { prompt: fill(prompt)!, help: fill(help) };
}

export type SurveyQuestion =
  | (SurveyQuestionBase & {
      type: "scale";
      min: 1;
      max: 5;
      minLabel: string;
      maxLabel: string;
    })
  | (SurveyQuestionBase & { type: "single"; options: SurveyOption[] })
  | (SurveyQuestionBase & {
      type: "multi";
      options: SurveyOption[];
      maxPicks?: number;
    })
  | (SurveyQuestionBase & { type: "nps" })
  | (SurveyQuestionBase & { type: "text"; maxLength: number });

// Shared by the before/after attitude pair. Favorable first.
const AI_ATTITUDE_OPTIONS: SurveyOption[] = [
  { value: "excited", label: "Excited to use it" },
  { value: "open", label: "Open to it" },
  { value: "unsure", label: "Unsure" },
  { value: "skeptical", label: "Skeptical" },
  { value: "against", label: "Against it" },
];
// Attitude values that count as "open to AI" for the before/after stats.
export const AI_ATTITUDE_OPEN_VALUES = ["excited", "open"];

export const SURVEY_QUESTIONS: readonly SurveyQuestion[] = [
  {
    id: "ai_attitude_before",
    type: "single",
    prompt: "Before this trial, how did you feel about using AI for report writing?",
    feedbackPrompt:
      "Before you started using Police Narratives AI, how did you feel about AI for report writing?",
    options: AI_ATTITUDE_OPTIONS,
  },
  {
    id: "ai_attitude_after",
    type: "single",
    prompt: "And now, after using it?",
    options: AI_ATTITUDE_OPTIONS,
  },
  {
    id: "overall",
    type: "scale",
    prompt: "Overall, how would you rate Police Narratives AI?",
    min: 1,
    max: 5,
    minLabel: "Poor",
    maxLabel: "Excellent",
  },
  {
    id: "quality_vs_manual",
    type: "single",
    prompt: "Compared to reports you write without it, reports from PNAI are…",
    // Favorable answer first on every worded list (numeric scales stay
    // ascending, which is the convention officers expect).
    options: [
      { value: "much_better", label: "Much better" },
      { value: "somewhat_better", label: "Somewhat better" },
      { value: "about_the_same", label: "About the same" },
      { value: "somewhat_worse", label: "Somewhat worse" },
      { value: "much_worse", label: "Much worse" },
    ],
  },
  {
    id: "quality_improvements",
    type: "multi",
    prompt: "What improved about your reports?",
    help: "Select all that apply",
    options: [
      { value: "accuracy", label: "Accuracy and completeness" },
      { value: "grammar", label: "Grammar and spelling" },
      { value: "structure", label: "Structure and organization" },
      { value: "consistency", label: "Consistency with department standards" },
      { value: "fewer_kickbacks", label: "Fewer reports sent back by supervisors" },
      { value: "other", label: "Other", allowText: true },
      { value: "nothing", label: "Nothing noticeable" },
    ],
  },
  {
    id: "time_saved_per_report",
    type: "single",
    prompt: "On average, how much time did it save you per report?",
    options: [
      { value: "over_2_hours", label: "More than 2 hours", midpoint: 150 },
      { value: "1_2_hours", label: "1–2 hours", midpoint: 90 },
      { value: "30_60", label: "30–60 minutes", midpoint: 45 },
      { value: "15_30", label: "15–30 minutes", midpoint: 22 },
      { value: "under_15", label: "Under 15 minutes", midpoint: 10 },
      { value: "none", label: "None", midpoint: 0 },
    ],
  },
  {
    id: "overtime",
    type: "single",
    prompt: "Did it reduce overtime you spend on reports?",
    options: [
      { value: "significantly", label: "Yes, significantly" },
      { value: "somewhat", label: "Yes, somewhat" },
      { value: "no_change", label: "No change" },
      { value: "not_applicable", label: "I don't work report overtime" },
    ],
  },
  {
    id: "time_reinvested",
    type: "multi",
    prompt: "What did you do with the time you got back?",
    help: "Select all that apply",
    options: [
      { value: "patrol", label: "More time on patrol or in the field" },
      { value: "before_end_of_shift", label: "Finished reports before end of shift" },
      { value: "thorough_investigations", label: "More thorough investigations" },
      { value: "home_on_time", label: "Went home on time" },
      { value: "other", label: "Other", allowText: true },
      { value: "nothing", label: "Nothing changed" },
    ],
  },
  {
    id: "ease_of_use",
    type: "scale",
    prompt: "How easy was it to learn and use?",
    min: 1,
    max: 5,
    minLabel: "Very hard",
    maxLabel: "Very easy",
  },
  {
    id: "time_to_comfort",
    type: "single",
    prompt: "How long before you felt comfortable using it?",
    options: [
      { value: "first_report", label: "My first report" },
      { value: "first_shift", label: "My first shift" },
      { value: "first_week", label: "My first week" },
      { value: "longer", label: "Longer than a week" },
      { value: "never", label: "Never got comfortable" },
    ],
  },
  {
    id: "favorite_features",
    type: "multi",
    prompt: "Which features were most useful?",
    help: "Pick up to 4",
    maxPicks: 4,
    options: [
      { value: "dictation", label: "Dictate a narrative and get a draft" },
      { value: "interaction_audio", label: "Body cam and interview transcription" },
      { value: "enhanced_writing", label: "Enhanced writing" },
      { value: "suggestions", label: "Suggestions and report score" },
      { value: "templates", label: "Report templates" },
      { value: "snippets", label: "Snippets" },
      { value: "forms", label: "PDF forms" },
      { value: "mobile", label: "Mobile app" },
      { value: "checklists", label: "Checklists" },
    ],
  },
  {
    id: "concerns",
    type: "multi",
    prompt: "Anything that held you back?",
    help: "Select all that apply",
    options: [
      { value: "nothing", label: "Nothing" },
      { value: "draft_accuracy", label: "Accuracy of AI drafts" },
      { value: "transcription_errors", label: "Transcription errors" },
      { value: "processing_time", label: "Processing time" },
      { value: "learning_curve", label: "Learning curve" },
      { value: "privacy", label: "Privacy or data concerns" },
      { value: "other", label: "Other", allowText: true },
    ],
  },
  {
    id: "recommend",
    type: "nps",
    prompt: "How likely are you to recommend it to another officer?",
  },
  {
    id: "keep",
    type: "single",
    prompt: "Should {agency} keep it?",
    options: [
      { value: "yes", label: "Yes, definitely" },
      { value: "probably", label: "Probably" },
      { value: "not_sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "quote",
    type: "text",
    prompt: "What would you tell your chief about it?",
    help: "Optional. A sentence or two is plenty. Quotes appear with your name in the report to {agency}.",
    maxLength: SURVEY_QUOTE_MAX_CHARS,
  },
];

export const SURVEY_QUESTION_COUNT = SURVEY_QUESTIONS.length;

export type SurveyAnswerValue = number | string | string[];
export type SurveyAnswers = Partial<Record<SurveyAnswerId, SurveyAnswerValue>> &
  Partial<Record<SurveyOtherKey, string>>;

export type SurveyStatus = "in_progress" | "completed" | "skipped";
// manual = staff opened a survey window for the agency ("Send survey now").
export type SurveyTrigger = "final_week" | "post_expiry" | "manual";
export type SurveyPlatform = "web" | "mobile";

export function getSurveyQuestion(id: SurveyQuestionId): SurveyQuestion {
  const q = SURVEY_QUESTIONS.find((question) => question.id === id);
  if (!q) throw new Error(`Unknown survey question: ${id}`);
  return q;
}

export function optionLabel(id: SurveyQuestionId, value: string): string {
  const q = getSurveyQuestion(id);
  if (q.type !== "single" && q.type !== "multi") return value;
  return q.options.find((o) => o.value === value)?.label ?? value;
}

// ---------- Validation ----------

export type SurveyValidation =
  | { ok: true; answers: SurveyAnswers }
  | { ok: false; errors: string[] };

function isInt(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v);
}

// Validates a partial answer object. Unknown ids, values that aren't one of
// the question's options, too many picks, and out-of-range numbers are all
// rejected so nothing unexpected ever lands in the answers jsonb.
export function validateSurveyAnswers(input: unknown): SurveyValidation {
  if (input === undefined || input === null) return { ok: true, answers: {} };
  if (typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, errors: ["answers must be an object"] };
  }
  const errors: string[] = [];
  const answers: SurveyAnswers = {};
  for (const [key, raw] of Object.entries(input as Record<string, unknown>)) {
    // "<id>_other": free text for an option flagged allowText.
    if (key.endsWith("_other")) {
      const baseId = key.slice(0, -"_other".length);
      const base = SURVEY_QUESTIONS.find((question) => question.id === baseId);
      const allowed =
        base && (base.type === "single" || base.type === "multi") && base.options.some((o) => o.allowText);
      if (!allowed) {
        errors.push(`Unknown question: ${key}`);
        continue;
      }
      if (raw === null || raw === undefined) continue;
      if (typeof raw !== "string") {
        errors.push(`${key} must be a string`);
        continue;
      }
      const trimmed = raw.trim();
      if (trimmed.length > SURVEY_OTHER_MAX_CHARS) {
        errors.push(`${key} must be ${SURVEY_OTHER_MAX_CHARS} characters or fewer`);
        continue;
      }
      if (trimmed) answers[key as SurveyOtherKey] = trimmed;
      continue;
    }
    const q = SURVEY_QUESTIONS.find((question) => question.id === key);
    if (!q || q.type === "text") {
      errors.push(`Unknown question: ${key}`);
      continue;
    }
    // null/undefined clears an answer (used when the officer changes their mind).
    if (raw === null || raw === undefined) continue;
    const id = q.id as SurveyAnswerId;
    switch (q.type) {
      case "scale": {
        if (!isInt(raw) || raw < q.min || raw > q.max) {
          errors.push(`${key} must be an integer between ${q.min} and ${q.max}`);
        } else {
          answers[id] = raw;
        }
        break;
      }
      case "nps": {
        if (!isInt(raw) || raw < 0 || raw > 10) {
          errors.push(`${key} must be an integer between 0 and 10`);
        } else {
          answers[id] = raw;
        }
        break;
      }
      case "single": {
        if (typeof raw !== "string" || !q.options.some((o) => o.value === raw)) {
          errors.push(`${key} has an invalid option`);
        } else {
          answers[id] = raw;
        }
        break;
      }
      case "multi": {
        if (!Array.isArray(raw) || raw.some((v) => typeof v !== "string")) {
          errors.push(`${key} must be an array of option values`);
          break;
        }
        const values = Array.from(new Set(raw as string[]));
        if (values.some((v) => !q.options.some((o) => o.value === v))) {
          errors.push(`${key} has an invalid option`);
          break;
        }
        if (q.maxPicks && values.length > q.maxPicks) {
          errors.push(`${key} allows at most ${q.maxPicks} picks`);
          break;
        }
        answers[id] = values;
        break;
      }
    }
  }
  return errors.length ? { ok: false, errors } : { ok: true, answers };
}

export function validateQuote(
  text: unknown
): { ok: true; value: string | null } | { ok: false; error: string } {
  if (text === undefined || text === null) return { ok: true, value: null };
  if (typeof text !== "string") return { ok: false, error: "quote must be a string" };
  const trimmed = text.trim();
  if (!trimmed) return { ok: true, value: null };
  if (trimmed.length > SURVEY_QUOTE_MAX_CHARS) {
    return { ok: false, error: `quote must be ${SURVEY_QUOTE_MAX_CHARS} characters or fewer` };
  }
  return { ok: true, value: trimmed };
}

// ---------- Progress helpers (shared by the web + mobile steppers) ----------

export function isQuestionAnswered(
  q: SurveyQuestion,
  answers: SurveyAnswers,
  quote?: string | null
): boolean {
  if (q.type === "text") return Boolean(quote && quote.trim());
  const v = answers[q.id as SurveyAnswerId];
  if (v === undefined || v === null) return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

export function countAnswered(answers: SurveyAnswers, quote?: string | null): number {
  return SURVEY_QUESTIONS.filter((q) => isQuestionAnswered(q, answers, quote)).length;
}

// Index of the first unanswered question, so a reopened survey resumes where
// the officer left off. Returns the last index when everything is answered.
export function firstUnansweredIndex(answers: SurveyAnswers, quote?: string | null): number {
  const idx = SURVEY_QUESTIONS.findIndex((q) => !isQuestionAnswered(q, answers, quote));
  return idx === -1 ? SURVEY_QUESTIONS.length - 1 : idx;
}

// ---------- Aggregates (produced server-side, rendered on web) ----------

export type RankedOption = {
  value: string;
  label: string;
  count: number;
  // Percent of officers who answered this question (0-100, rounded).
  pct: number;
};

// Quotes are always attributed by name in the department report.
export type SurveyQuote = {
  text: string;
  name: string | null;
  rank: string | null;
  badge: string | null;
};

export type SurveyAggregates = {
  version: number;
  // Completed responses — everything below is computed from these.
  responses: number;
  // Completed + in-progress rows (staff view only; the brief passes 0).
  started: number;
  avg_overall: number | null; // 1-5
  // Attitude toward AI report writing before vs. after the trial.
  ai_attitude: {
    before: RankedOption[];
    after: RankedOption[];
    pct_open_before: number | null; // excited | open
    pct_open_after: number | null;
    // Officers who were unsure/skeptical/against before, and how many of
    // them are excited/open now.
    skeptics: number;
    skeptics_won_over: number;
    pct_skeptics_won_over: number | null;
  };
  pct_quality_better: number | null; // somewhat_better | much_better
  time_saved: { buckets: RankedOption[]; weighted_avg_min: number | null };
  pct_overtime_reduced: number | null; // significantly | somewhat, of those it applies to
  time_reinvested: RankedOption[];
  avg_ease: number | null; // 1-5
  time_to_comfort: RankedOption[];
  top_features: RankedOption[]; // ranked by count desc
  concerns: RankedOption[]; // ranked by count desc, "nothing" excluded
  nps: {
    score: number | null; // -100..100
    promoters: number;
    passives: number;
    detractors: number;
    answered: number;
  };
  keep: { pct_yes_or_probably: number | null; distribution: RankedOption[] };
  per_question: Partial<
    Record<
      SurveyAnswerId,
      {
        answered: number;
        distribution: RankedOption[];
        // Free text officers typed next to an "Other" option.
        other_texts?: string[];
      }
    >
  >;
  quotes: SurveyQuote[];
};
